import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { getAgentChatSettings } from '~/features/agents/utils'
import useGetLatestFormSubmission from '~/features/chatbotV2/hooks/useGetLatestFormSubmission'
import { EPreChatFormKeys } from '~/features/preChatForms/models'
import { store } from '~/store'

type TParams = {
  guestToken: string | null
  isSessionReady: boolean
  refreshGuestToken?: () => Promise<string | null>
}

const useChatAreaReady = ({
  guestToken,
  isSessionReady,
  refreshGuestToken,
}: TParams) => {
  const chatSettings = store((state) => state.chatbot.agent.chatSettings)
  const form = getAgentChatSettings(chatSettings)?.form

  const hasPreChatFormConfigured = useMemo(() => {
    const fields = form?.[EPreChatFormKeys.fields] ?? []
    return !!form && fields.length > 0
  }, [form])

  const {
    isFetched: isLatestFormSubmissionFetched,
    isError: isLatestFormSubmissionError,
  } = useGetLatestFormSubmission({
    guestToken,
    refreshGuestToken,
    queryOptions: {
      enabled: hasPreChatFormConfigured,
    },
  })

  const isPriorSubmissionQuerySettled =
    isLatestFormSubmissionFetched || isLatestFormSubmissionError

  const [isChatAreaReady, setIsChatAreaReady] = useState(false)

  const resetChatAreaReady = useCallback(() => {
    setIsChatAreaReady(false)
  }, [])

  useEffect(() => {
    if (isChatAreaReady) return
    if (!isSessionReady) return

    if (
      hasPreChatFormConfigured &&
      (!guestToken || !isPriorSubmissionQuerySettled)
    ) {
      return
    }

    setIsChatAreaReady(true)
  }, [
    guestToken,
    hasPreChatFormConfigured,
    isChatAreaReady,
    isPriorSubmissionQuerySettled,
    isSessionReady,
  ])

  useEffect(() => {
    if (!isChatAreaReady || !hasPreChatFormConfigured) return
    if (!isLatestFormSubmissionError) return

    toast.error('Could not load the form. You can continue chatting.')
  }, [hasPreChatFormConfigured, isChatAreaReady, isLatestFormSubmissionError])

  const isWaitingForChatReady =
    !isChatAreaReady &&
    (!isSessionReady ||
      (hasPreChatFormConfigured &&
        !!guestToken &&
        !isPriorSubmissionQuerySettled))

  return {
    isWaitingForChatReady,
    resetChatAreaReady,
  }
}

export default useChatAreaReady
